import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';
import { Instagram } from 'lucide-react';

const BLUE = '#47ABDB';

const SectionWrapper = styled.section`
  padding: 60px 20px;
  @media (max-width: 768px) {
    padding: 40px 15px;
  }
`;

const Inner = styled.div`
  max-width: 1700px;
  margin: 0 auto;
  text-align: center;
`;

const IconBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #4e4094, #14cbde);
  color: #fff;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  color: #111D4E;
  font-size: 2.4rem;
  margin: 0 0 .5rem;
  display: inline-block;
  padding-bottom: 8px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);
  background-size: 100% 3px;
  background-position: 0 bottom;
  background-repeat: no-repeat;
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const SectionSubtitle = styled.p`
  color: #5B6B78;
  font-size: 1.05rem;
  margin: 1rem 0 2rem;
`;

const FollowLink = styled.a`
  color: ${BLUE};
  font-weight: 700;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: .5rem;
  }
`;

const Post = styled.a`
  position: relative;
  display: block;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #eef2f7;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .08);
`;

const PostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .3s ease;
  ${Post}:hover & {
    transform: scale(1.06);
  }
`;

// Public homepage section - reads the cached feed heartscms keeps in sync
// with Instagram, never Instagram itself. Renders nothing if the feature
// isn't enabled/configured yet or there's no cached content, so it's safe to
// leave wired into the homepage before the integration is fully set up.
const InstagramFeed = () => {
  const intl = useIntl();
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_API_URL}/api/cms/instagram/public/feed`)
      .then(({ data }) => { if (!cancelled) setFeed(data); })
      .catch(() => { if (!cancelled) setFeed(null); });
    return () => { cancelled = true; };
  }, []);

  if (!feed || !feed.enabled || !feed.items || feed.items.length === 0) {
    return null;
  }

  return (
    <SectionWrapper>
      <Inner>
        <IconBadge>
          <Instagram size={26} />
        </IconBadge>
        <div>
          <SectionTitle>
            <FormattedMessage id="instagramFeed.title" defaultMessage="Follow us on Instagram" />
          </SectionTitle>
        </div>
        <SectionSubtitle>
          {feed.profileUrl ? (
            <FollowLink href={feed.profileUrl} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="instagramFeed.follow" defaultMessage="View our profile" />
            </FollowLink>
          ) : (
            <FormattedMessage id="instagramFeed.subtitle" defaultMessage="A look at recent moments from HEARTS." />
          )}
        </SectionSubtitle>
        <Grid>
          {feed.items.map((item, index) => (
            <Post
              key={item.permalink || index}
              href={item.permalink}
              target="_blank"
              rel="noopener noreferrer"
              title={item.caption || undefined}
            >
              <PostImage
                src={item.displayUrl}
                alt={item.caption || intl.formatMessage({ id: 'instagramFeed.title', defaultMessage: 'Follow us on Instagram' })}
                loading="lazy"
              />
            </Post>
          ))}
        </Grid>
      </Inner>
    </SectionWrapper>
  );
};

export default InstagramFeed;
